import Dexie, { type Table } from 'dexie'
import type { Submission, DraftRecording, Settings, RecordingChunk } from '@/types'
import type { TaxonomyPayload } from '@/types/taxonomy'

export interface TaxonomyCacheRecord {
  id: string
  version: string | null
  updatedAt: string | null
  payload: TaxonomyPayload
  cachedAt: string
}

interface SettingsRecord {
  key: string
  value: Settings
}

export class InsightRecorderDB extends Dexie {
  submissions!: Table<Submission, string>
  drafts!: Table<DraftRecording, string>
  settings!: Table<SettingsRecord, string>
  taxonomy!: Table<TaxonomyCacheRecord, string>
  chunks!: Table<RecordingChunk, string>

  constructor() {
    super('insight-recorder')
    this.version(1).stores({
      submissions: 'id, createdAt, type, status',
      drafts: 'id',
      settings: 'key',
      taxonomy: 'id',
      chunks: 'id, sessionId',
    })

    // v2: add tusUploadUrl field to chunks (no index change needed)
    this.version(2).stores({
      submissions: 'id, createdAt, type, status',
      drafts: 'id',
      settings: 'key',
      taxonomy: 'id',
      chunks: 'id, sessionId',
    })
  }
}

export const db = new InsightRecorderDB()

export async function initDB(): Promise<InsightRecorderDB> {
  if (db.isOpen()) {
    return db
  }

  try {
    await db.open()
    return db
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error)
    const errorName = error instanceof Error ? error.name : ''
    const isVersionError =
      errorMessage.includes('lower version') ||
      errorMessage.includes('lower than the existing version') ||
      errorName === 'VersionError' ||
      (errorName === 'InvalidStateError' && errorMessage.includes('version'))

    if (isVersionError) {
      console.warn(
        '[DB] Version mismatch detected. Deleting and recreating database.',
        errorMessage,
      )
      db.close()
      await db.delete()
      await db.open()
      return db
    }
    throw error
  }
}

export function resetDbConnections(): void {
  try {
    db.close()
  } catch {
    // ignore
  }
}
